import { apiClient } from './api';

export type JobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface QueueItem {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'paused' | 'stalled';
  job_counts: {
    queued: number;
    running: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  created_at: string;
}

export interface JobItem {
  id: string;
  queue_id: string;
  job_name: string;
  tenant_id: string;
  tenant_name: string;
  type: string;
  status: JobStatus;
  progress: number;
  started_at?: string;
  estimated_completion?: string;
  processed_items?: number;
  total_items?: number;
  created_at: string;
  error_message?: string;
}

export interface QueueStats {
  total_queues: number;
  total_jobs: number;
  queued: number;
  running: number;
  completed: number;
  failed: number;
}

export interface TenantOption {
  tenant_id: string;
  tenant_name: string;
}

export interface QueueFilters {
  type?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface JobFilters {
  tenant_id?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface PagedMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PagedResponse<T> {
  data: T[];
  meta: PagedMeta;
}

const normalizePaged = <T>(payload: Record<string, unknown>): { data: T[]; meta: PagedMeta } => {
  const items = (payload?.items ?? []) as T[];
  const meta = (payload?.meta ?? {
    page: 1,
    limit: items.length,
    total: items.length,
    total_pages: 1,
  }) as PagedMeta;
  return {
    data: Array.isArray(items) ? items : [],
    meta,
  };
};

export const queueMonitorService = {
  getStats: async (): Promise<QueueStats> => {
    const response = await apiClient.get('/queue-monitor/stats');
    return response.data as QueueStats;
  },

  getTenants: async (): Promise<TenantOption[]> => {
    const response = await apiClient.get('/queue-monitor/tenants');
    return response.data as TenantOption[];
  },

  getQueues: async (filters: QueueFilters = {}): Promise<PagedResponse<QueueItem>> => {
    const response = await apiClient.get('/queue-monitor/queues', { params: filters });
    return normalizePaged<QueueItem>(response.data);
  },

  getQueueById: async (id: string): Promise<QueueItem> => {
    const response = await apiClient.get(`/queue-monitor/queues/${id}`);
    return response.data as QueueItem;
  },

  pauseQueue: async (id: string): Promise<void> => {
    await apiClient.post(`/queue-monitor/queues/${id}/pause`);
  },

  resumeQueue: async (id: string): Promise<void> => {
    await apiClient.post(`/queue-monitor/queues/${id}/resume`);
  },

  getJobsInQueue: async (queueId: string, filters: JobFilters = {}): Promise<PagedResponse<JobItem>> => {
    const response = await apiClient.get(`/queue-monitor/queues/${queueId}/jobs`, { params: filters });
    return normalizePaged<JobItem>(response.data);
  },

  getJobById: async (id: string): Promise<JobItem> => {
    const response = await apiClient.get(`/queue-monitor/jobs/${id}`);
    return response.data as JobItem;
  },

  cancelJob: async (id: string): Promise<void> => {
    await apiClient.post(`/queue-monitor/jobs/${id}/cancel`);
  },

  retryJob: async (id: string): Promise<void> => {
    await apiClient.post(`/queue-monitor/jobs/${id}/retry`);
  },
};

export default queueMonitorService;